/**
 * Data layer for `goal-reports`. Typed fetch wrappers over /api/v1/goal-reports.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Surfaces the bulk /reports endpoint that drives the dashboard view.
 */

export type GoalMetric = "conversions" | "revenue" | "clicks";
export type GoalScope = "global" | "campaign" | "affiliate";
export type GoalStatus = "on_track" | "at_risk" | "off_track" | "complete";
export type PeriodState = "pending" | "active" | "ended";

export interface GoalReportRow {
	goal_id: string;
	name: string;
	metric: GoalMetric;
	target_value: number;
	actual_total: number;
	pct_complete: number;
	days_elapsed: number;
	days_total: number;
	days_remaining: number;
	elapsed_fraction: number;
	projected_eot: number;
	status: GoalStatus;
	period_state: PeriodState;
	as_of: number;
}

export interface GoalReportsQueryArgs {
	metric?: GoalMetric | "all";
	scope?: GoalScope | "all";
	active_now?: boolean;
	limit?: number;
	offset?: number;
}

export interface GoalReportsResponse {
	items: GoalReportRow[];
	limit: number;
	offset: number;
}

const BASE = "/api/v1/goal-reports";
const TENANT_HEADER = "x-tenant-id";
const DEFAULT_TENANT = "t1";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

/**
 * Bulk list of goal reports for the current tenant.
 * Hits GET /api/v1/goal-reports/reports.
 */
export async function listGoalReports(
	args: GoalReportsQueryArgs = {},
): Promise<GoalReportsResponse> {
	const params = new URLSearchParams();
	if (args.metric && args.metric !== "all") params.set("metric", args.metric);
	if (args.scope && args.scope !== "all") params.set("scope", args.scope);
	if (args.active_now) params.set("active_now", "1");
	if (typeof args.limit === "number") params.set("limit", String(args.limit));
	if (typeof args.offset === "number") params.set("offset", String(args.offset));
	const qs = params.toString();
	const url = `${BASE}/reports${qs ? `?${qs}` : ""}`;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			[TENANT_HEADER]: DEFAULT_TENANT,
		},
	});
	return parseOrThrow<GoalReportsResponse>(res);
}

export const goalReportsQueryKeys = {
	all: ["goal-reports"] as const,
	list: (args: GoalReportsQueryArgs) =>
		[
			"goal-reports",
			"reports",
			args.metric ?? "all",
			args.scope ?? "all",
			args.active_now ? "active" : "any",
			args.limit ?? 200,
			args.offset ?? 0,
		] as const,
};
